import React, { useState } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { registerInterest } from '../../util/api';
import { Form, PrimaryButton, FieldTextInput } from '../../components';

import css from './WaitlistForm.module.css';

// Post-launch register-interest form (replaces the pre-launch waitlist picker): visitors OUTSIDE
// LA/OC leave name, email, city/ZIP, role, and optional comments. Submissions are stored as LEADS
// (the provider_waitlist Postgres table via /api/register-interest) -- separate from account
// signups -- so demand can be measured by market and "we've launched in your city" emails sent.
const ROLES = ['parent', 'provider'];

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RegisterInterestForm = () => {
  const intl = useIntl();
  const [status, setStatus] = useState(null); // null | 'sending' | 'sent' | 'error'

  const requiredMsg = intl.formatMessage({ id: 'WaitlistPage.interest.required' });
  const validate = values => {
    const errors = {};
    if (!values.name?.trim()) errors.name = requiredMsg;
    if (!values.email?.trim()) {
      errors.email = requiredMsg;
    } else if (!emailRe.test(values.email.trim())) {
      errors.email = intl.formatMessage({ id: 'WaitlistPage.interest.invalidEmail' });
    }
    if (!values.city?.trim()) errors.city = requiredMsg;
    if (!values.role) errors.role = requiredMsg;
    return errors;
  };

  const onSubmit = values => {
    setStatus('sending');
    return registerInterest({
      name: values.name?.trim(),
      email: values.email?.trim(),
      city: values.city?.trim(),
      role: values.role,
      comments: values.comments?.trim() || null,
      // Honeypot field rides along; humans never fill it.
      website: values.website || '',
    })
      .then(() => setStatus('sent'))
      .catch(() => setStatus('error'));
  };

  if (status === 'sent') {
    return (
      <p className={css.successMessage}>
        <FormattedMessage id="WaitlistPage.interest.success" />
      </p>
    );
  }

  return (
    <FinalForm
      onSubmit={onSubmit}
      validate={validate}
      render={({ handleSubmit, values }) => (
        <Form className={css.form} onSubmit={handleSubmit}>
          <FieldTextInput
            id="RegisterInterest.name"
            name="name"
            type="text"
            label={intl.formatMessage({ id: 'WaitlistPage.interest.nameLabel' })}
          />
          <FieldTextInput
            id="RegisterInterest.email"
            name="email"
            type="email"
            className={css.spaced}
            label={intl.formatMessage({ id: 'WaitlistPage.interest.emailLabel' })}
          />
          <FieldTextInput
            id="RegisterInterest.city"
            name="city"
            type="text"
            className={css.spaced}
            label={intl.formatMessage({ id: 'WaitlistPage.interest.cityLabel' })}
          />

          <div className={css.roleGroup}>
            {/* A real <label> element, so it carries EXACTLY the same styles as the input labels. */}
            <label id="RegisterInterest.roleLabel">
              <FormattedMessage id="WaitlistPage.interest.roleLabel" />
            </label>
            <Field name="role">
              {({ input }) => (
                <div
                  className={css.regionRow}
                  role="radiogroup"
                  aria-labelledby="RegisterInterest.roleLabel"
                >
                  {ROLES.map(role => {
                    const selected = input.value === role;
                    return (
                      <button
                        key={role}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className={classNames(css.regionPill, { [css.regionSelected]: selected })}
                        onClick={() => input.onChange(role)}
                      >
                        <FormattedMessage id={`WaitlistPage.interest.role.${role}`} />
                      </button>
                    );
                  })}
                </div>
              )}
            </Field>
          </div>

          <FieldTextInput
            id="RegisterInterest.comments"
            name="comments"
            type="textarea"
            className={css.spaced}
            label={intl.formatMessage({ id: 'WaitlistPage.interest.commentsLabel' })}
          />

          {/* Honeypot: visually hidden; bots auto-fill it and the server silently drops those. */}
          <div className={css.honeypot} aria-hidden="true">
            <Field name="website" component="input" type="text" tabIndex={-1} autoComplete="off" />
          </div>

          {status === 'error' ? (
            <p className={css.errorMessage}>
              <FormattedMessage id="WaitlistPage.interest.error" />
            </p>
          ) : null}

          <PrimaryButton
            type="submit"
            className={css.submit}
            inProgress={status === 'sending'}
            disabled={
              status === 'sending' ||
              Object.keys(validate(values)).length > 0
            }
          >
            <FormattedMessage id="WaitlistPage.interest.submit" />
          </PrimaryButton>
          <p className={css.finePrint}>
            <FormattedMessage id="WaitlistPage.interest.finePrint" />
          </p>
        </Form>
      )}
    />
  );
};

export default RegisterInterestForm;
